/**
 * ConversationRpcClient - AtomRpc integration for conversation endpoints
 *
 * This uses AtomRpc.Tag to create a special context tag that provides
 * proper integration with the atom runtime.
 *
 * Uses NDJSON serialization to support streaming responses for future
 * message streaming functionality.
 *
 * The ClerkTokenGetter layer reads the `__session` cookie directly (see
 * `readClerkSessionCookieToken`), falling back to `globalThis.__clerkSession`
 * when the cookie isn't present.
 *
 * @example
 * ```typescript
 * const conversationsAtom = ConversationRpcClient.runtime.atom((get) =>
 *   Effect.gen(function*() {
 *     const client = yield* ConversationRpcClient;
 *     const conversations = yield* client("conversation.list", void 0);
 *     return conversations;
 *   })
 * );
 * ```
 *
 * @since 1.0.0
 */
import { AtomRpc } from "@effect-atom/atom-react";
import { FetchHttpClient } from "@effect/platform";
import { RpcSerialization } from "@effect/rpc";
import { ConversationRpcs } from "@fm/api/rpc";
import { Layer } from "effect";
import { makeRpcClientLayer, makeTokenGetterLayer } from "./shared.ts";

export class ConversationRpcClient extends AtomRpc.Tag<ConversationRpcClient>()(
  "ConversationRpcClient",
  {
    group: ConversationRpcs,
    protocol: makeRpcClientLayer(ConversationRpcs).pipe(
      Layer.provide(RpcSerialization.layerNdjson),
      Layer.provide(makeTokenGetterLayer()),
      Layer.provide(FetchHttpClient.layer),
    ),
  },
) {}
